"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SearchSelectInput = void 0;
const react_1 = __importStar(require("react"));
const resize_observer_1 = require("@juggle/resize-observer");
const es_toolkit_1 = require("es-toolkit");
const __1 = require("../../../../..");
const hooks_1 = require("../../../../../shared/hooks");
const tokens_1 = require("../../../../../styles/tokens");
const config_1 = require("../../config");
const hooks_2 = require("../../hooks");
const utils_1 = require("../../utils");
const SelectInput_styles_1 = require("../SelectInput/SelectInput.styles");
const SelectedItems_1 = require("../SelectInput/SelectedItems");
const SelectList_styles_1 = require("../SelectList/SelectList.styles");
const FilteredOptions_1 = require("./FilteredOptions");
const SelectLoadingState_1 = require("./SelectLoadingState");
// When loading on server-side (e.g. in a Next.js app) we need to account for a lack of window
const ON_CLIENT = typeof window !== "undefined";
const ResizeObserver = ON_CLIENT ? window.ResizeObserver || resize_observer_1.ResizeObserver : resize_observer_1.ResizeObserver;
const SearchSelectInput = ({ searchMode, showLoading, loadingText, }) => {
    var _a, _b;
    const wrapper = (0, react_1.useRef)(null);
    const innerWrapper = (0, react_1.useRef)(null);
    const { id, disabled = false, onChangeHandler, selected = [], error = false, zIndex = 1, expandCallback, expanded = false, setExpanded = () => [], setSelected = () => [], dimension = "large", options, emptyGroup, displayLimit = 100, shouldDisplayValues, valueDescription, initSelectedValues, theme = "DEFAULT", defaultCopy, handleCallbacks, } = (0, hooks_2.useSelect)();
    const listRef = (0, react_1.useRef)(null);
    const [focusIndex, setFocusIndex] = (0, react_1.useState)(1); // for focussing on items
    const [hasFocus, setHasFocus] = (0, react_1.useState)(false);
    const [hasHover, setHasHover] = (0, react_1.useState)(false);
    const [inputHeight, setInputHeight] = (0, react_1.useState)(0);
    const [searchInput, setSearchInput] = (0, react_1.useState)("");
    const isFirstRender = (0, hooks_1.useFirstRender)();
    const isSelectedItemArray = Array.isArray(selected);
    // When there's a single initSelectedValue and it's the first render don't focus
    const shouldFocusOnSelect = !(initSelectedValues && isFirstRender);
    (0, react_1.useEffect)(() => {
        var _a;
        if (theme === "DEFAULT" && isSelectedItemArray) {
            return;
        }
        if (selected && !isFirstRender && onChangeHandler) {
            onChangeHandler(selected, {
                searchInput,
                groupedOptions,
            });
        }
        if (selected && shouldFocusOnSelect && theme === "DEFAULT") {
            (_a = wrapper === null || wrapper === void 0 ? void 0 : wrapper.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [selected]);
    // Combined function for handling keys for opening, escaping and navigating
    // This is so that ARROW_DOWN action can be implemented properly (i.e. arrow to open doesn't also move the nav further down the list)
    // N.B. The check for focusIndex !== 1 is because when in the search box (which has focusIndex 1) the left/right arrow keys should move left/right in the search term text
    const handleKeyDown = (event) => {
        if (expanded) {
            if (event.code === config_1.KEYS.ESCAPE) {
                setExpanded && setExpanded(false);
                setSearchInput(""); // need to reset the search term when opening/closing
                handleCollapseCallback();
            }
            else if (event.code === config_1.KEYS.ARROW_UP ||
                event.key === config_1.KEYS.IE_ARROW_UP ||
                ((event.code === config_1.KEYS.ARROW_LEFT ||
                    event.key === config_1.KEYS.IE_ARROW_LEFT) &&
                    focusIndex !== 1)) {
                setFocusIndex((0, utils_1.calculateNewFocus)(groupedOptions, focusIndex, -1, true));
            }
            else if (event.code === config_1.KEYS.ARROW_DOWN ||
                event.key === config_1.KEYS.IE_ARROW_DOWN ||
                ((event.code === config_1.KEYS.ARROW_RIGHT ||
                    event.key === config_1.KEYS.IE_ARROW_RIGHT) &&
                    focusIndex !== 1)) {
                setFocusIndex((0, utils_1.calculateNewFocus)(groupedOptions, focusIndex, 1, true));
            }
        }
        else {
            setExpanded && setExpanded(true);
        }
    };
    (0, hooks_1.useKey)([
        config_1.KEYS.ENTER,
        config_1.KEYS.ESCAPE,
        config_1.KEYS.ARROW_DOWN,
        config_1.KEYS.ARROW_UP,
        config_1.KEYS.ARROW_LEFT,
        config_1.KEYS.ARROW_RIGHT,
        config_1.KEYS.IE_ARROW_DOWN,
        config_1.KEYS.IE_ARROW_UP,
        config_1.KEYS.IE_ARROW_LEFT,
        config_1.KEYS.IE_ARROW_RIGHT,
    ], handleKeyDown, {
        target: wrapper,
    });
    (0, react_1.useEffect)(() => {
        var _a, _b;
        if (expanded) {
            (_b = (_a = wrapper === null || wrapper === void 0 ? void 0 : wrapper.current) === null || _a === void 0 ? void 0 : _a.querySelector(`[tabIndex='${focusIndex}']`)) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [focusIndex, expanded]);
    const ro = new ResizeObserver((0, es_toolkit_1.debounce)((entries) => {
        setInputHeight(entries[0].target.clientHeight);
    }, 0));
    (0, react_1.useEffect)(() => {
        if (innerWrapper.current && !inputHeight) {
            setInputHeight(innerWrapper.current.clientHeight);
            ro.observe(innerWrapper.current);
        }
        return () => {
            ro.disconnect();
        };
    }, []);
    const handleFocus = () => {
        setHasFocus(true);
    };
    const handleMouseEnter = () => {
        setHasHover(true);
    };
    const handleMouseLeave = () => {
        setHasHover(false);
    };
    const handleCollapseCallback = () => {
        (handleCallbacks === null || handleCallbacks === void 0 ? void 0 : handleCallbacks.onCollapse) && handleCallbacks.onCollapse(selected);
    };
    const handleRemoveCallback = (selected) => {
        (handleCallbacks === null || handleCallbacks === void 0 ? void 0 : handleCallbacks.onRemove) && handleCallbacks.onRemove(selected);
    };
    const handleClearAllCallback = () => {
        (handleCallbacks === null || handleCallbacks === void 0 ? void 0 : handleCallbacks.onClearAll) && handleCallbacks.onClearAll();
    };
    const handleBlur = (event) => {
        /**
         * WORKAROUND : IE11 : relatedTarget is not available in IE11, using activeElement as a fallback
         */
        const target = event.relatedTarget || document.activeElement;
        if (!event.currentTarget.contains(target) && expanded) {
            setExpanded && setExpanded(false);
            setHasFocus(false);
            setHasHover(false);
            handleCollapseCallback();
        }
    };
    const toggleExpanded = (event) => {
        expandCallback && expandCallback();
        setSearchInput(""); // need to reset the search term when opening/closing
        event && event.stopPropagation();
        setExpanded && setExpanded(!expanded);
    };
    const expandHandler = () => {
        // Want to be able to click on closed to expand, but if already expanded want to be able to click into the search box
        if (!expanded) {
            toggleExpanded();
        }
    };
    const resetHandler = (event) => {
        var _a;
        event.stopPropagation();
        setSelected([]);
        setSearchInput(""); // remove search text on clear all click
        handleClearAllCallback();
        (_a = wrapper === null || wrapper === void 0 ? void 0 : wrapper.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const handleChangeSearchTerm = (event) => {
        setSearchInput(event.target.value);
        setExpanded(true);
        setFocusIndex(1);
    };
    const defaultHeader = "";
    const groupedOptions = (0, utils_1.getGroupedOptions)(options, defaultHeader, searchMode, searchInput);
    if (emptyGroup && groupedOptions.length === 0) {
        groupedOptions.push(emptyGroup);
    }
    const placeholderText = expanded
        ? (_a = defaultCopy === null || defaultCopy === void 0 ? void 0 : defaultCopy.placeholder) !== null && _a !== void 0 ? _a : "Search for an option"
        : (_b = defaultCopy === null || defaultCopy === void 0 ? void 0 : defaultCopy.placeholder) !== null && _b !== void 0 ? _b : "Please select";
    return (react_1.default.createElement(SelectInput_styles_1.StyledSelectWrapper, { height: inputHeight && inputHeight, zIndex: zIndex, marginBottom: "2px" },
        react_1.default.createElement(SelectInput_styles_1.StyledSelect, { tabIndex: 0, "data-testid": `select-${id}`, "aria-labelledby": id, "aria-expanded": expanded, "aria-readonly": true, "aria-disabled": disabled, ref: wrapper, onFocus: handleFocus, onBlur: handleBlur, onClick: expandHandler, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, disabled: disabled, error: error, expanded: expanded },
            react_1.default.createElement("div", { ref: innerWrapper },
                theme !== "DEFAULT" && isSelectedItemArray && (react_1.default.createElement(SelectInput_styles_1.StyledSearchMultiSelectWrapper, null,
                    react_1.default.createElement(SelectInput_styles_1.StyledMultiSelectSelectedList, null,
                        selected.length > 0 &&
                            selected.map((option, i) => {
                                return (react_1.default.createElement(SelectedItems_1.SelectedItems, { key: i, option: option, onRemoveCallback: (selected) => handleRemoveCallback(selected) }));
                            }),
                        (expanded || selected.length === 0) && (react_1.default.createElement(SelectInput_styles_1.StyledSearchText, null,
                            react_1.default.createElement(SelectInput_styles_1.StyledMultiSelectSearch, { id: id, type: "search", value: searchInput, autoFocus: expanded, placeholder: placeholderText, onChange: handleChangeSearchTerm, tabIndex: 0, disabled: disabled, expanded: expanded })))),
                    selected.length > 0 && (react_1.default.createElement(SelectInput_styles_1.StyledSelectIconCross, { "data-testid": "select-reset", onClick: (event) => {
                            !disabled && resetHandler(event);
                        }, disabled: disabled, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, dimension: dimension },
                        react_1.default.createElement(__1.Icon, { name: "Cross", title: "Clear all", size: tokens_1.buttonInputFieldIconSizes[config_1.DIMENSIONS.MEDIUM].withText, colour: hasHover && !disabled
                                ? "red"
                                : "stone", props: {
                                focusable: false,
                            } }))))),
                theme === "DEFAULT" &&
                    (expanded ||
                        (isSelectedItemArray && selected.length === 0)) && (react_1.default.createElement(SelectInput_styles_1.StyledSelectSearch, { id: id, dimension: dimension, type: "search", value: searchInput, autoFocus: expanded, placeholder: expanded
                        ? "Search for an option"
                        : "Please select", onChange: handleChangeSearchTerm, tabIndex: 0, disabled: disabled, expanded: expanded })),
                !expanded && selected && !isSelectedItemArray && (react_1.default.createElement(SelectInput_styles_1.StyledSelectText, { dimension: dimension }, selected.label)),
                react_1.default.createElement(SelectInput_styles_1.StyledSelectIcon, { "data-testid": "select-toggle", onClick: (event) => toggleExpanded(event), disabled: disabled, dimension: dimension, theme: theme },
                    react_1.default.createElement(__1.Icon, { name: "Arrow", size: tokens_1.buttonInputFieldIconSizes[config_1.DIMENSIONS.MEDIUM]
                            .withText, colour: (0, utils_1.iconColours)(disabled, hasFocus, hasHover, false), rotation: expanded && !disabled ? "up" : "down", props: {
                            "aria-hidden": true,
                            focusable: false,
                        } }))),
            expanded && !disabled && (react_1.default.createElement(SelectList_styles_1.StyledSelectListWrapper, { ref: listRef, "data-testid": "select-list", role: "select" }, showLoading ? (react_1.default.createElement(SelectLoadingState_1.SelectLoadingState, { loadingText: loadingText })) : (react_1.default.createElement(SelectList_styles_1.StyledSelectList, null,
                react_1.default.createElement(FilteredOptions_1.FilteredOptions, { groupedOptions: groupedOptions, displayLimit: displayLimit, shouldDisplayValues: shouldDisplayValues !== null && shouldDisplayValues !== void 0 ? shouldDisplayValues : false, valueDescription: valueDescription, onListItemClick: () => setSearchInput("") }))))))));
};
exports.SearchSelectInput = SearchSelectInput;
//# sourceMappingURL=SearchSelectInput.js.map